import { useControllerAppModel } from '@/controller/models/ControllerAppModel';
import { ClassNames } from '@emotion/react';
import { MaturityLevel } from '@toilet-paper-games/core';
import {
  Card,
  Dialog,
  FlexColumn,
  FlexRow,
  fullWidth,
  Slider,
  TitledCard,
} from '@toilet-paper-games/ui';
import { Smartphone, Tv } from 'lucide-react';
import { action, reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';

const VolumeSettings = observer(() => {
  const appStore = useControllerAppModel();
  const controllerGlobalSettings = appStore.controllerGlobalSettings;
  const hosterGlobalSettings = appStore.hosterGlobalSettings;

  const isHost = !!appStore.user?.isHost;

  if (!controllerGlobalSettings || !hosterGlobalSettings) {
    return null;
  }

  return (
    <FlexColumn gap={10}>
      <FlexColumn css={[fullWidth]} gap={10}>
        <FlexRow justifyContent="space-between" alignItems="center" gap={10}>
          <span>Sound Fx</span>
          <Tv />
        </FlexRow>
        <SettingSlider
          disabled={!isHost}
          getCurrentValue={() => hosterGlobalSettings.soundFxVolume}
          updateValue={(v) => appStore.updateHosterGlobalSettings('soundFxVolume', v)}
        />
      </FlexColumn>
      <FlexColumn css={[fullWidth]} gap={10}>
        <FlexRow justifyContent="space-between" alignItems="center" gap={10}>
          <span>Narration</span>
          <Tv />
        </FlexRow>
        <SettingSlider
          disabled={!isHost}
          getCurrentValue={() => hosterGlobalSettings.narrationVolume}
          updateValue={(v) => appStore.updateHosterGlobalSettings('narrationVolume', v)}
        />
      </FlexColumn>
      <FlexColumn css={[fullWidth]} gap={10}>
        <FlexRow justifyContent="space-between" alignItems="center" gap={10}>
          <span>Music</span>
          <Tv />
        </FlexRow>
        <SettingSlider
          disabled={!isHost}
          getCurrentValue={() => hosterGlobalSettings.musicVolume}
          updateValue={(v) => appStore.updateHosterGlobalSettings('musicVolume', v)}
        />
      </FlexColumn>
      <FlexColumn css={[fullWidth]} gap={10}>
        <FlexRow justifyContent="space-between" alignItems="center" gap={10}>
          <span>Sound Fx</span>
          <Smartphone />
        </FlexRow>
        <SettingSlider
          getCurrentValue={() => controllerGlobalSettings.soundFxVolume}
          updateValue={(v) => appStore.updateControllerGlobalSettings('soundFxVolume', v)}
        />
      </FlexColumn>
    </FlexColumn>
  );
});

interface SettingSliderProps {
  disabled?: boolean;
  getCurrentValue: () => number;
  updateValue: (value: number) => void;
}

const SettingSlider = observer((props: SettingSliderProps) => {
  const { disabled, getCurrentValue, updateValue } = props;

  const [value, setValue] = useState(getCurrentValue);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (isDragging) {
      return;
    }

    let reactionDisposer: (() => void) | undefined;
    const timeout = setTimeout(() => {
      return reaction(
        getCurrentValue,
        (v) => {
          if (!isDragging) {
            setValue(v);
          }
        },
        { fireImmediately: true },
      );
    }, 500);

    return () => {
      clearTimeout(timeout);
      reactionDisposer && reactionDisposer();
    };
  }, [getCurrentValue, isDragging]);

  return (
    <Slider
      disabled={disabled}
      css={[
        fullWidth,
        disabled && {
          filter: 'brightness(0.5)',
          pointerEvents: 'none',
        },
      ]}
      value={value}
      onValueChange={([v]) => {
        setIsDragging(true);
        setValue(v);
      }}
      onValueCommit={([v]) => {
        setIsDragging(false);
        updateValue(v);
      }}
    />
  );
});

const MaturitySettings = observer(() => {
  const appStore = useControllerAppModel();
  const globalSettings = appStore.hosterGlobalSettings;

  const [value, setValue] = useState(globalSettings?.maturityLevel);

  useEffect(() => {
    if (!globalSettings) {
      return;
    }

    return reaction(
      () => globalSettings.maturityLevel,
      (v) => setValue(v),
      { fireImmediately: true },
    );
  }, [globalSettings]);

  const updateValue = useCallback(
    (v: MaturityLevel) => {
      setValue(v);
      appStore.updateHosterGlobalSettings('maturityLevel', v);
    },
    [appStore, setValue],
  );

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value as MaturityLevel;
    updateValue(selectedValue);
  };

  return (
    <FlexColumn css={[fullWidth]} gap={7}>
      <select
        disabled
        value={value}
        onChange={handleChange}
        css={{
          width: '100%',
          padding: '10px',
          borderRadius: '8px',
          border: '1px solid #c7c7c7',
          backgroundColor: '#385b71',
          color: 'white',
          fontSize: '14px',
          outline: 'none',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: '#3e657d',
            borderColor: '#c7c7c7',
          },
          '&:focus': {
            borderColor: '#c7c7c7',
            boxShadow: '0 0 0 2px rgba(199, 199, 199, 0.5)',
          },
          '&>option': {
            backgroundColor: '#385b71',
            color: 'white',
            padding: '10px',
          },
          '&[disabled]': {
            backgroundColor: '#3e657d',
            color: 'white',
            cursor: 'not-allowed',
            '&>option': {
              backgroundColor: '#3e657d',
              color: 'white',
            },
          },
        }}
      >
        {Object.values(MaturityLevel).map((level) => (
          <option key={level} value={level}>
            {level}
          </option>
        ))}
      </select>
    </FlexColumn>
  );
});

const SettingsDialogContent = observer(() => {
  const appStore = useControllerAppModel();
  const globalSettings = appStore.controllerGlobalSettings;

  if (!globalSettings) {
    return null;
  }

  return (
    <FlexColumn gap={25} css={[{ paddingTop: 10 }, fullWidth]}>
      <TitledCard title="Volume" inset>
        <VolumeSettings />
      </TitledCard>
      <TitledCard title="Maturity" inset>
        <MaturitySettings />
      </TitledCard>
    </FlexColumn>
  );
});

// SettingsDialog Component
export interface SettingsDialogProps {}

export const SettingsDialog = observer((props: SettingsDialogProps) => {
  const appStore = useControllerAppModel();

  // Extract the Dialog's open state and handler outside of ClassNames
  const isDialogOpen = appStore.uiModel.showSettingsDialog;
  const handleDialogOpenChange = action((open: boolean) => {
    appStore.uiModel.showSettingsDialog = open;
  });

  return (
    <ClassNames>
      {({ css }) => (
        <Dialog
          open={isDialogOpen}
          title="Settings"
          onOpenChange={handleDialogOpenChange}
          cardProps={{
            className: css({
              width: 'min(calc(100% - 20px), 300px)',
            }),
          }}
        >
          <SettingsDialogContent />
        </Dialog>
      )}
    </ClassNames>
  );
});
